import { delivery } from '@bb/contracts';
import { Button, Field, Input, Select } from '@bb/ui';
import { useMemo, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import {
  addVariant,
  CHANNEL_LABELS,
  copyVariantContent,
  isContentEmpty,
  issuesAt,
  removeVariant,
  renameVariant,
  setControlPercent,
  setVariantApiId,
  setVariantContent,
  setVariantPercent,
  splitEvenly,
  totalPercent,
  validateMessagesDraft,
  type ChannelContent,
  type DeliveryChannel,
  type MessagesDraft,
} from './messagesDraft.ts';

/** Slot for a channel composer (cards BB-62, push BB-73, IAM BB-85) inside a variant. */
export type RenderChannelContent = (variantApiId: string, channel: DeliveryChannel, content: ChannelContent, setContent: (next: ChannelContent) => void, readOnly: boolean) => ReactNode;

export interface VariantsEditorProps {
  value: MessagesDraft;
  onChange: (next: MessagesDraft) => void;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
  /** Content editor per variant and channel; by default a short summary is shown. */
  renderContent?: RenderChannelContent;
}

const formatPercent = (percent: number) => (Number.isFinite(percent) ? String(percent) : '');
const parsePercent = (text: string) => (text.trim() === '' ? 0 : Number(text));

/** A share in percent; keeps the typed text while the value it means stays the same. */
function PercentInput({ value, onValueChange, ...rest }: { value: number; onValueChange: (percent: number) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const [text, setText] = useState(() => formatPercent(value));
  const [synced, setSynced] = useState(value);
  if (!Object.is(value, synced)) {
    setSynced(value);
    if (!Object.is(parsePercent(text), value)) setText(formatPercent(value));
  }
  return (
    <Input
      {...rest}
      type="number"
      inputMode="decimal"
      min={0}
      max={100}
      step={0.01}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onValueChange(parsePercent(e.target.value));
      }}
    />
  );
}

const summary = (content: ChannelContent | undefined) => {
  if (isContentEmpty(content)) return 'empty';
  const n = Object.keys(content ?? {}).length;
  return `${n} field${n === 1 ? '' : 's'}`;
};

/**
 * Variants of a campaign with their shares and the no-message control (BB-98, AN-004): up to eight variants, each
 * with content for every channel of the campaign; shares of variants and control add up to 100 %.
 */
export function VariantsEditor({ value, onChange, readOnly = false, renderContent }: VariantsEditorProps) {
  const issues = useMemo(() => validateMessagesDraft(value).issues, [value]);
  const [copySource, setCopySource] = useState<Record<number, string>>({});
  const [newFrom, setNewFrom] = useState('');
  const total = totalPercent(value);
  const full = value.variants.length >= delivery.MAX_VARIANTS;
  const isCard = value.channels.includes('content_card');
  const variantOptions = (except?: number) => value.variants.flatMap((v, i) => (i === except ? [] : [{ value: String(i), label: v.name || `Variant ${i + 1}` }]));

  const copy = (target: number) => {
    const source = Number(copySource[target]);
    if (copySource[target] === undefined || copySource[target] === '' || !value.variants[source]) return;
    onChange(copyVariantContent(value, target, source));
    setCopySource((s) => ({ ...s, [target]: '' }));
  };

  const add = () => {
    const source = newFrom === '' ? undefined : Number(newFrom);
    onChange(addVariant(value, source !== undefined && value.variants[source] ? source : undefined));
    setNewFrom('');
  };

  return (
    <section className="stack-form" aria-label="Variants">
      <h3>Variants</h3>
      {value.variants.map((variant, index) => {
        const title = variant.name || `Variant ${index + 1}`;
        const first = (...path: Array<string | number>) => issuesAt(issues, 'variants', index, ...path)[0];
        return (
          <fieldset key={index}>
            <legend>{title}</legend>
            <Field label="Name" error={first('name')}>
              <Input value={variant.name} disabled={readOnly} onChange={(e) => onChange(renameVariant(value, index, e.target.value))} />
            </Field>
            <Field label="API id" hint="Used in reports and the API. Follows the name until you edit it." error={first('apiId')}>
              <Input value={variant.apiId} disabled={readOnly} spellCheck={false} onChange={(e) => onChange(setVariantApiId(value, index, e.target.value))} />
            </Field>
            <Field label="Share, %" error={first('percent')}>
              <PercentInput value={variant.percent} disabled={readOnly} onValueChange={(p) => onChange(setVariantPercent(value, index, p))} />
            </Field>
            <div aria-label={`Content of ${title}`} role="group">
              {value.channels.length === 0 && <p className="hint">Choose a channel to add content.</p>}
              {value.channels.map((channel) => {
                const content = variant.content[channel];
                const error = first('content', channel);
                return (
                  <div key={channel}>
                    {renderContent ? (
                      renderContent(variant.apiId, channel, content ?? {}, (next) => onChange(setVariantContent(value, index, channel, next)), readOnly)
                    ) : (
                      <p>
                        {CHANNEL_LABELS[channel]}: {summary(content)}
                      </p>
                    )}
                    {error && (
                      <p className="bb-field__error" role="alert">
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
              {Object.keys(variant.content)
                .filter((c) => !value.channels.includes(c as DeliveryChannel))
                .map((c) => (
                  <p key={c} className="bb-field__error" role="alert">
                    {first('content', c)}
                  </p>
                ))}
            </div>
            {value.variants.length > 1 && (
              <div className="inline-form">
                <Field label="Copy content from…">
                  <Select
                    value={copySource[index] ?? ''}
                    placeholder="Choose a variant"
                    options={variantOptions(index)}
                    disabled={readOnly}
                    onChange={(e) => setCopySource((s) => ({ ...s, [index]: e.target.value }))}
                  />
                </Field>
                <Button size="sm" disabled={readOnly || !copySource[index]} onClick={() => copy(index)}>
                  Copy
                </Button>
              </div>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={readOnly || value.variants.length <= 1}
              onClick={() => {
                setCopySource({});
                setNewFrom('');
                onChange(removeVariant(value, index));
              }}
            >
              Remove
            </Button>
          </fieldset>
        );
      })}

      <fieldset>
        <legend>Control</legend>
        <Field
          label="Control — gets nothing"
          hint={
            <>
              Share of recipients, in %, who get no message: the baseline for conversions.
              {isCard && ' Control users of a Content Card still log an impression when the card’s placement is visible, so both groups convert on the same window.'}
            </>
          }
          error={issuesAt(issues, 'control', 'percent')[0]}
        >
          <PercentInput value={value.control.percent} disabled={readOnly} onValueChange={(p) => onChange(setControlPercent(value, p))} />
        </Field>
      </fieldset>

      <p>
        Total: <output aria-label="Total share">{total}%</output>
      </p>
      {issuesAt(issues, 'variants').map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}

      <div className="actions">
        <Button disabled={readOnly} onClick={() => onChange(splitEvenly(value))}>
          Split evenly
        </Button>
      </div>
      <div className="inline-form">
        <Field label="New variant content" hint={full ? `A campaign has at most ${delivery.MAX_VARIANTS} variants.` : undefined}>
          <Select value={newFrom} options={[{ value: '', label: 'Empty' }, ...variantOptions().map((o) => ({ ...o, label: `Copy of ${o.label}` }))]} disabled={readOnly || full} onChange={(e) => setNewFrom(e.target.value)} />
        </Field>
        <Button disabled={readOnly || full} onClick={add}>
          Add variant
        </Button>
      </div>
      {readOnly && <p className="hint">This revision is launched: its variants and shares cannot change.</p>}
    </section>
  );
}
